import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import Toaster from "../Toaster/Toaster";
import Loader from "../../Components/Loader/Loader";
import { TextField, Autocomplete, Chip } from '@mui/material';
import { MaterialReactTable } from "material-react-table";
import {
  handleApiError,
  showNotification,
  showConfirmNotification
} from "../../actions/notificationActions";

import { FetchAdvisorFirmMultiUser } from '../../Apis/FetchAdvisorFirmMultiUser';
import { AddAdvisorFirmMultiUser } from '../../Apis/AddAdvisorFirmMultiUser';
import { DeleteAdvisorFirmMultiUser } from '../../Apis/DeleteAdvisorFirmMultiUser';
import { UpdateAdvisorFirmMultiUser } from '../../Apis/UpdateAdvisorFirmMultiUser';
import { ResendEmailInviteAdvisorFirmMultiUser } from '../../Apis/ResendEmailInviteAdvisorFirmMultiUser';
import SkeletonComponent from '../Skeleton/Skeleton';


const AddMultiUsers = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(false);
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [addAdvisorFirmUser, setAddAdvisorFirmUser] = useState([]);
    const [advisorFirmUsers, setAdvisorFirmUsers] = useState([]);

    const subscriptionsDetails = useSelector(state => state.userSubscription.subscriptionData) || {};
    const advisorProfile = useSelector(state => state.advisorProfile.profileData);

    const [containsOwnEmail, setContainsOwnEmail] = useState(false);

    const [failedEmailAdd, setFailedEmailAdd] = useState([]);
    const [showFailedEmailAdd, setShowFailedEmailAdd] = useState(false);

    const [showAlmostThere, setShowAlmostThere] = useState(false);
    React.useEffect(() => {
        let timer;
        if (isLoading) {
            timer = setTimeout(() => {setShowAlmostThere(true);}, 1750);
        } else {
            setShowAlmostThere(false);
        }
        return () => clearTimeout(timer);
    }, [isLoading]);

    const fetchAdvisorFirmUsers = useCallback(async () => {
    try {
        const response = await FetchAdvisorFirmMultiUser();
        if (response.status === 200) {
        setAdvisorFirmUsers(response.result || []);
        } else {
        handleApiError(response, dispatch);
        }
    } catch (error) {
        handleApiError(error, dispatch);
    }
    }, [dispatch]);

    useEffect(() => {
        const loadUsers = async () => {
        setIsLoading(true);
        await fetchAdvisorFirmUsers();
        setIsLoading(false);
        };
        loadUsers();
    }, [fetchAdvisorFirmUsers]);

    useEffect(() => {
        const updatedFirmUser = emails
        .filter((email) => isValidEmail(email) && email !== advisorProfile.emailid)
        .map((email) => ({
            advisor_username: advisorProfile.emailid,
            user_email: email,
        }));
        setAddAdvisorFirmUser(updatedFirmUser);
        const hasOwnEmail = emails.includes(advisorProfile.emailid?.toLowerCase());
        setContainsOwnEmail(hasOwnEmail);

    }, [emails, advisorProfile]);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleAddEmails = (input) => {
        const parsed = input
            .split(/[\s,;\n]+/)
            .map((email) => email.trim().toLowerCase())
            .filter((email) => email.length > 0);

        setEmails((prev) => {
            const merged = [...prev, ...parsed];
            return Array.from(new Set(merged));
        });
    };

    const extractFailedEmailsWithReasons = (message) => {
      const emailRegex = /[\w.-]+@[\w.-]+\.\w+/g;
      const emails = message.match(emailRegex) || [];
      const failedUsers = emails.map(email => ({
        email,
        reason: 'Already have Advisor Firm Subscription active as Admin'
      }));
      setFailedEmailAdd(failedUsers);
    };

    const handleAddUsers = useCallback(async () => {
      try {
          setIsLoading(true);
          const res = await AddAdvisorFirmMultiUser(addAdvisorFirmUser);
          if (res.status === 200 && res.result?.status_code === 200) {
              await fetchAdvisorFirmUsers();
              setEmails([]);
              if (res.result.message.includes("could not be added because")) {
                  extractFailedEmailsWithReasons(res.result.message)
                  setShowFailedEmailAdd(true);
                  dispatch(showNotification("Users added successfully. Some were skipped due to active subscriptions as Admin.", "success", "toaster"));
              } else {
                  dispatch(showNotification("Users added successfully", "success", "toaster"));
              }
          } else {
              dispatch(showNotification("Failed to add users", "error", "toaster"));
          }
      } catch (error) {
          handleApiError(error, dispatch);
      } finally {
          setIsLoading(false);
      }
    }, [addAdvisorFirmUser, dispatch, fetchAdvisorFirmUsers]);

     const handleDeleteAdvisorFirmUser = useCallback(async (useremail) => {
        try {
          setIsLoading(true);
          const res = await DeleteAdvisorFirmMultiUser(useremail);
          if (res.status === 200) {
            await fetchAdvisorFirmUsers();
            dispatch(showNotification("User deleted successfully", "success","toaster"));
          } else {
              handleApiError(res, dispatch);
          }
        } catch (error) {
          handleApiError(error, dispatch);
        } finally {
          setIsLoading(false);
        }
    }, [dispatch, fetchAdvisorFirmUsers]);

    const handleUpdateAdvisorFirmUser = useCallback(async (useremail,action) => {
        try {
          setIsLoading(true);
          const res = await UpdateAdvisorFirmMultiUser(useremail,action);
          if (res.status === 200) {
            await fetchAdvisorFirmUsers();
            dispatch(showNotification(`User account ${action}d successfully`, "success","toaster"));
          } else {
              handleApiError(res, dispatch);
          }
        } catch (error) {
          handleApiError(error, dispatch);
        } finally {
          setIsLoading(false);
        }
    }, [dispatch, fetchAdvisorFirmUsers]);

    const handleUserDelete = useCallback((useremail) => {
      dispatch(
          showConfirmNotification(
              "Are you sure you want to delete this user?",
              () => handleDeleteAdvisorFirmUser(useremail)
          )
      );
    }, [dispatch, handleDeleteAdvisorFirmUser]);

    const handleUserStatus = useCallback((useremail,action) => {
      dispatch(
        showConfirmNotification(
          `Are you sure you want to ${action} this user ?`,
          () => handleUpdateAdvisorFirmUser(useremail,action)
        )
      );
    }, [dispatch, handleUpdateAdvisorFirmUser])

    const handleResendEmailAdvisorFirmUser = useCallback(async (useremail) => {
        try {
          setIsLoading(true);
          const res = await ResendEmailInviteAdvisorFirmMultiUser(useremail);
          if (res.status === 200) {
            dispatch(showNotification(`Email notification send to user successfully`, "success","toaster"));
          } else {
              handleApiError(res, dispatch);
          }
        } catch (error) {
          handleApiError(error, dispatch);
        } finally {
          setIsLoading(false);
        }
    }, [dispatch]);
    
    const handleResendInvite = useCallback((useremail) => {
      dispatch(
          showConfirmNotification(
              "Are you sure you want to resend email invite to this user?",
              () => handleResendEmailAdvisorFirmUser(useremail)
          )
      );
    }, [dispatch, handleResendEmailAdvisorFirmUser]);

    const columns = useMemo(
      () => [
        { accessorKey: "sr", header: "#", size: 70 },
        { accessorKey: "emailid", header: "User Email", size: 250 },
        { accessorKey: "invitestatusLabel", header: "Invite Status", size: 250 },
        { accessorKey: "adminapprovalstatusLabel", header: "Admin Approval (Previous)", size: 250 },
        {
          accessorKey: "actions",
          enableSorting: false,
          header: "Actions",
          Cell: ({ row }) => {
            const isActive = row.original.userstatus;
            const toggleLabel = isActive ? "Disable" : "Enable";

            return (
              <>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => handleUserDelete(row.original.emailid)}
                    className="ai-btn small icon-only"
                    disabled={isLoading}
                    title="Delete User"
                  >
                    Delete
                  </button>
                  {row.original.invitestatusLabel === 'Accepted' && (
                    <button
                      onClick={() => handleUserStatus(row.original.emailid, toggleLabel)}
                      className={isActive?"ai-btn red solid": "ai-btn blue solid"}
                      disabled={isLoading}
                    >
                      {toggleLabel} User
                    </button>
                  )}
                  {row.original.invitestatusLabel !== 'Accepted'  && (
                    <button
                      onClick={() => handleResendInvite(row.original.emailid)}
                      className="ai-btn primary solid"
                      disabled={isLoading}
                    >
                      Resend Invite
                    </button>
                  )}
                </div>
              </>
            );
          },
        },
      ],
      [handleUserDelete, handleUserStatus, isLoading]
    );

    const data = useMemo(
      () =>
        advisorFirmUsers.map((value, index) => ({
          sr: index + 1,
          emailid: value.useremail,
          invitestatusLabel: value.invite_status,
          adminapprovalstatusLabel: value.admin_approval_status ? value.admin_approval_status : "N/A",
          userstatus: value.user_status
        })),
      [advisorFirmUsers]
    );

  return (
    <>
      {isLoading && (
        <div className="loading-overlay">
            <SkeletonComponent variant="multiuser" showAlmostThere={showAlmostThere} />
        </div>
      )}
      <div className="profiledata">
        <div className="toast-container">
          <Toaster />
        </div>
        <div className="profileoption">
          <div className="plan">
            <div className="planhead">
              <h3>Add User Email</h3>
            </div>
            <div className="planinfo">
              <div>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={emails}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    if (/[,\s;]/.test(newInputValue.slice(-1))) {
                      handleAddEmails(newInputValue);
                      setInputValue("");
                    } else {
                      setInputValue(newInputValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    const deduped = Array.from(
                      new Set(
                        newValue
                          .map((e) => e.trim().toLowerCase())
                          .filter(Boolean)
                      )
                    );
                    setEmails(deduped);
                  }}
                  filterSelectedOptions
                  disableClearable
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const isOwnEmail = option === advisorProfile.emailid?.toLowerCase();
                      const isValid = isValidEmail(option) && !isOwnEmail;

                      const chipProps = {
                        variant: isValid ? "outlined" : "filled",
                        color: isValid ? "default" : "error",
                        label: option,
                        ...getTagProps({ index }),
                        sx: {
                          backgroundColor: isValid ? "transparent" : "red",
                          "& .MuiChip-deleteIcon": {
                            color: isValid ? "default" : "white",
                          },
                          "&:hover .MuiChip-deleteIcon": {
                            color: isValid ? "red" : "white",
                          },
                        },
                      };
                      return <Chip key={option} {...chipProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && inputValue) {
                          handleAddEmails(inputValue);
                          setInputValue("");
                          e.preventDefault();
                        }
                      }}
                      onBlur={(e) => {
                      if (inputValue.trim()) {
                        handleAddEmails(inputValue);
                        setInputValue("");
                      }
                    }}
                      onPaste={(e) => {
                        const paste = e.clipboardData.getData("text");
                        handleAddEmails(paste);
                        setInputValue("");
                        e.preventDefault();
                      }}
                      variant="outlined"
                      placeholder={emails.length === 0 ? "Type or paste emails" : ""}
                      helperText={
                          containsOwnEmail
                            ? "Cannot add your own email address."
                            : emails.some((e) => !isValidEmail(e))
                            ? "Some emails are invalid"
                            : emails.length === 0 && isValidEmail(inputValue.trim()) && inputValue.trim() !== ""
                            ? "Press Space, Comma, or Enter to confirm the email"
                            : "Separate emails with comma, space, or semicolon"
                        }
                       sx={{
                        width: "100%",
                        display: "inline-flex",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 150,
                        maxWidth: "calc(100% - 10px)",
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          minWidth: '150px',
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  )}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    width: "100%",
                  }}
                />
              </div>
            </div>
            <div className="cancelsub">
              <button
                className={isLoading || addAdvisorFirmUser.length === 0 || containsOwnEmail? "ai-btn primary solid disabled" : "ai-btn primary solid"}
                onClick={handleAddUsers}
                disabled={isLoading || addAdvisorFirmUser.length === 0 || containsOwnEmail}
              >
                {isLoading ? "Adding..." : "Add Users"}
              </button>
            </div>
          </div>
          {showFailedEmailAdd && (
            <div className="plan">
              <div className="planhead">
                <h3>Failed Email Additions</h3>
              </div>
              <div className="planinfo">
                {failedEmailAdd.map(({ email, reason }, index) => (
                  <div className="failed-point" key={index}>
                    <strong>{email}</strong>
                    <em>{reason}</em>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="risk-contribution-table">
            <div className="portfolio-table contributio-table ">
              <div className="table_holder table_head ai-toggle-con m0i">
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  enableStickyHeader
                  enableStickyFooter
                  enableBottomToolbar={false}
                  enableColumnActions={false}
                  enableDensityToggle={false}
                  enablePagination={false}
                  enableFullScreenToggle={false}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "400px",
                    },
                  }}
                  initialState={{
                    density: "compact",
                  }}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                  muiTableFootCellProps={{
                    sx: {
                      backgroundColor: "white",
                      position: "sticky",
                      bottom: 0,
                      zIndex: 1,
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMultiUsers;